import { useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { $generateHtmlFromNodes } from "@lexical/html";
import { TRANSFORMERS } from "@lexical/markdown";
import {
  MentionPlugin,
  ToolbarPlugin,
  AutoLinkPlugin,
  CodeHighlightPlugin,
} from "./Plugins";
// import TreeViewPlugin from "./plugins/TreeViewPlugin";
// import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
// import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import { MentionNode } from "./Plugins/MentionPlugin/MentionNode";
import "./style.css";

const exampleTheme = {
  ltr: "ltr",
  rtl: "rtl",
  placeholder: "editor-placeholder",
  paragraph: "editor-paragraph",
  quote: "editor-quote",
  heading: {
    h1: "editor-heading-h1",
    h2: "editor-heading-h2",
    h3: "editor-heading-h3",
    h4: "editor-heading-h4",
    h5: "editor-heading-h5",
  },
  list: {
    nested: {
      listitem: "editor-nested-listitem",
    },
    ol: "editor-list-ol",
    ul: "editor-list-ul",
    listitem: "editor-listitem",
  },
  image: "editor-image",
  link: "editor-link",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    overflowed: "editor-text-overflowed",
    hashtag: "editor-text-hashtag",
    underline: "editor-text-underline",
    strikethrough: "editor-text-strikethrough",
    underlineStrikethrough: "editor-text-underlineStrikethrough",
    code: "editor-text-code",
  },
  code: "editor-code",
};

function Placeholder() {
  return <div className="editor-placeholder">Enter some rich text...</div>;
}

const editorConfig = {
  // The editor theme
  theme: exampleTheme,
  // Handling of errors during update
  onError(error) {
    throw error;
  },
  // Any custom nodes go here
  nodes: [
    HeadingNode,
    ListNode,
    ListItemNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    AutoLinkNode,
    LinkNode,
    MentionNode,
  ],
};

const CustomOnChangePlugin = ({ value, onChange }) => {
  const [editor] = useLexicalComposerContext();
  return (
    <OnChangePlugin
      onChange={(editorState) => {
        editorState.read(() => {
          onChange($generateHtmlFromNodes(editor));
        });
      }}
    />
  );
};

export const LexicalTextEditor = () => {
  const [editorState, setEditorState] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [lastMessage, setLastMessage] = useState(null);
  const [messageHtml, setMessageHtml] = useState("");

  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files);
    const withType = files.map((f) => ({ file: f, type }));
    setAttachments((prev) => [...prev, ...withType]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if (!editorState && attachments.length === 0) return;

    //console.log("Sending message:");
    //console.log("Editor State:", editorState);
    //console.log("Attachments:", attachments);
    //console.log("Message HTML:", messageHtml);
    setLastMessage({
      editorState,
      attachments,
      timestamp: new Date(),
    });

    setAttachments([]);
  };

  return (
    <div
      className="lexical-text-editor"
      style={{ maxWidth: 600, margin: "80px auto" }}
    >
      {/* Show only latest message */}
      {/* {lastMessage && <ChatMessageView lastMessage={lastMessage} />} */}
      <div dangerouslySetInnerHTML={{ __html: editorState }}></div>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          maxWidth: 600,
          background: "#fff",
          justifyContent: "center",
        }}
      >
        <LexicalComposer initialConfig={editorConfig}>
          <div className="editor-container">
            <ToolbarPlugin />
            <div className="editor-inner">
              <RichTextPlugin
                contentEditable={<ContentEditable className="editor-input" />}
                placeholder={<Placeholder />}
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              <MentionPlugin />
              <AutoFocusPlugin />
              <ListPlugin />
              <LinkPlugin />
              <AutoLinkPlugin />
              <CodeHighlightPlugin />
              <CustomOnChangePlugin
                onChange={setEditorState}
                value={messageHtml}
              />
              <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            </div>
          </div>
        </LexicalComposer>

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              padding: "8px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            {attachments.map((att, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "6px",
                  position: "relative",
                  width: 80,
                }}
              >
                {att.type === "image" ? (
                  <img
                    src={URL.createObjectURL(att.file)}
                    alt=""
                    style={{
                      width: "100%",
                      height: 50,
                      objectFit: "cover",
                      borderRadius: 6,
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: "0.8rem",
                      textAlign: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {att.file.name}
                  </span>
                )}
                <button
                  onClick={() => removeAttachment(i)}
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    background: "#ef4444",
                    color: "#fff",
                    borderRadius: "50%",
                    border: "none",
                    width: 20,
                    height: 20,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Attachment Toolbar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: "1px solid #e5e7eb",
            padding: "6px 8px",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            {/* Image Upload */}
            <label style={{ cursor: "pointer" }}>
              🖼️
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e, "image")}
              />
            </label>

            {/* Video Upload */}
            <label style={{ cursor: "pointer" }}>
              🎥
              <input
                type="file"
                accept="video/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e, "video")}
              />
            </label>

            {/* Document Upload */}
            <label style={{ cursor: "pointer" }}>
              📄
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e, "doc")}
              />
            </label>
          </div>

          {/* Send Button (Dummy) */}
          <button
            style={{
              background: "#2563eb",
              color: "white",
              padding: "6px 14px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
            }}
            onClick={handleSend}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};
